import { lazy, Suspense, useEffect, useId, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";

import { Pagination, usePagination } from "../../../components/Pagination";
import { preparationHref } from "../../../lib/preparationNavigation";
import { useQuoteRequest, useQuoteRequestsByProject } from "../../../lib/queries/quoteRequests";
const CreateQuoteRequestDialog = lazy(() =>
  import("./CreateQuoteRequestDialog").then((module) => ({
    default: module.CreateQuoteRequestDialog,
  })),
);

/** Selection is URL state, never a new business state or a filter on project aggregates. */
export function RequestContext({
  projectId,
  children,
}: {
  projectId: string;
  children?: (requestId: string) => ReactNode;
}) {
  const { t, i18n } = useTranslation();
  const [createOpen, setCreateOpen] = useState(false);
  const inputId = useId();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const { hash } = useLocation();
  const pagination = usePagination();
  const requests = useQuoteRequestsByProject({ projectId, cursor: pagination.cursor });
  const requestedId = params.get("request") || undefined;
  const detail = useQuoteRequest(requestedId);
  const items = requests.data?.items ?? [];
  // A direct URL may refer to a request on another page. Check its project before displaying it.
  const selected = requestedId
    ? detail.data?.project_id === projectId
      ? detail.data
      : undefined
    : !requests.data?.next_cursor && !pagination.hasPrev && items.length === 1
      ? items[0]
      : undefined;
  const pending = requests.isLoading || (!!requestedId && detail.isLoading);
  const failed =
    requests.isError || (!!requestedId && (detail.isError || (!detail.isLoading && !selected)));
  const [verified, setVerified] = useState<{ projectId: string; requestId: string }>();
  const selectedId = selected?.id;
  useEffect(() => {
    if (!requestedId && selectedId && !pending && !failed) {
      const next = new URLSearchParams(params);
      next.set("request", selectedId);
      navigate({ search: next.toString(), hash }, { replace: true });
    }
  }, [requestedId, selectedId, pending, failed, params, hash, navigate]);
  useEffect(() => {
    if (!pending && !failed) {
      setVerified(selectedId ? { projectId, requestId: selectedId } : undefined);
    }
  }, [projectId, selectedId, pending, failed]);
  // A failed refresh must not discard local edits. Retain only this context's
  // previously verified workspace, hidden and disabled until verification succeeds.
  const workspaceId =
    !pending && !failed
      ? selectedId
      : verified?.projectId === projectId && (!requestedId || requestedId === verified.requestId)
        ? verified.requestId
        : undefined;
  const options =
    selected && !items.some((item) => item.id === selected.id) ? [selected, ...items] : items;

  return (
    <section aria-label={t("workspace.request_context")} className="space-y-3">
      {pending ? (
        <p role="status">{t("app.loading")}</p>
      ) : failed ? (
        <div role="alert" className="text-red-700">
          <p>{t("workspace.request_unavailable")}</p>
          <button
            type="button"
            className="btn min-h-11"
            onClick={() => {
              void requests.refetch();
              if (requestedId) void detail.refetch();
            }}
          >
            {t("app.retry")}
          </button>
        </div>
      ) : null}
      <div className="flex flex-wrap items-end gap-3">
        <label
          htmlFor={inputId}
          className="min-w-0 flex-1 basis-64 text-sm font-medium text-slate-700"
        >
          {t("workspace.request_context")}
          <select
            id={inputId}
            className="input mt-1 w-full min-w-0"
            value={selected?.id ?? ""}
            disabled={requests.isLoading || requests.isError}
            onChange={(event) => {
              const next = new URLSearchParams(params);
              next.delete("request_section");
              if (event.target.value) next.set("request", event.target.value);
              else next.delete("request");
              setParams(next);
            }}
          >
            <option value="">{t("workspace.choose_request")}</option>
            {options.map((item) => (
              <option key={item.id} value={item.id}>
                {item.title || t("requests.new.in_progress.untitled")} —{" "}
                {t(`quote_requests.statuses.${item.status}`)}
                {item.created_at && ` · ${new Date(item.created_at).toLocaleString(i18n.language)}`}
              </option>
            ))}
          </select>
        </label>
        {selected && !pending && !failed && !children && (
          <Link
            to={preparationHref(selected.id, params.get("request_section"))}
            className="bg-brand-600 hover:bg-brand-700 focus-visible:ring-brand-500 inline-flex min-h-11 items-center rounded-lg px-4 py-2 text-sm font-semibold text-white focus-visible:ring-2"
          >
            {t("workspace.open_request")}
          </Link>
        )}
      </div>
      {!pending &&
        !failed &&
        !!requests.data &&
        !items.length &&
        !selected &&
        !pagination.hasPrev &&
        !requests.data?.next_cursor && (
          <div className="space-y-3">
            <p className="mt-2 text-sm text-slate-600">{t("workspace.no_requests")}</p>
            <button type="button" className="btn min-h-11" onClick={() => setCreateOpen(true)}>
              {t("quote_requests.section.create_button")}
            </button>
            {createOpen && (
              <Suspense fallback={<p role="status">{t("app.loading")}</p>}>
                <CreateQuoteRequestDialog
                  projectId={projectId}
                  onClose={() => setCreateOpen(false)}
                />
              </Suspense>
            )}
          </div>
        )}
      {(pagination.hasPrev || requests.data?.next_cursor) && (
        <Pagination
          pageNumber={pagination.pageNumber}
          hasPrev={pagination.hasPrev}
          hasNext={!!requests.data?.next_cursor}
          onPrev={pagination.prev}
          onNext={() => pagination.next(requests.data?.next_cursor ?? null)}
          disabled={requests.isFetching}
        />
      )}
      {workspaceId && children && (
        <fieldset hidden={pending || failed} disabled={pending || failed} className="min-w-0">
          {children(workspaceId)}
        </fieldset>
      )}
    </section>
  );
}
